import * as React from 'react'

import { PackageIcon } from 'lucide-react'
import { MobXProviderContext, observer } from 'mobx-react'

import {
  IPermitTypeField,
  ITypeFieldFilterOptions,
  LocationType,
} from '~/client/graph/types.generated'
import { IResourceChipItem } from '~/client/src/shared/components/ResourceChipList'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/client/src/shared/components/shadcn-ui/popover'
import StruxhubResourceSelector from '~/client/src/shared/components/StruxhubInputs/StruxhubSelector/StruxhubResourceSelector'
import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import useMaterialResources from '~/client/src/shared/hooks/useMaterialResources'
import LocationBase from '~/client/src/shared/models/LocationObjects/LocationBase'
import { ResourceSemanticIconKey } from '~/client/src/shared/resources/definitions'
import { renderResourceRecordIcon } from '~/client/src/shared/resources/icons/resourceRecordIcon'
import { renderResourceSemanticIcon } from '~/client/src/shared/resources/icons/resourceSemanticIcons'
import type LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import type InitialState from '~/client/src/shared/stores/InitialState'

import ResourceTypeSelectionContent, {
  SelectableResourceTypeId,
} from '../../../../WorkflowFieldRestriction/variants/ResourceTypeSelectionContent'
import { useResourceValueSelection } from './useResourceValueSelection'

interface IProps {
  filter: ITypeFieldFilterOptions
  selectedField: IPermitTypeField
  isEditMode: boolean
  restrictedLocationIds: string[]
  restrictedLocationTypes: LocationType[]

  onChange(selectedFieldValues: string[]): void
}

const SEMANTIC_ICON: Record<SelectableResourceTypeId, ResourceSemanticIconKey> =
  {
    [ResourceTypeId.LOCATIONS]: 'location',
    [ResourceTypeId.OFFLOADING_EQUIPMENT]: 'equipment',
    [ResourceTypeId.MATERIALS]: 'material',
  }

const ICON_CLASS_NAME =
  'tw:block tw:size-4 tw:text-muted-foreground tw:ml-0.5 tw:mr-1'

const withResourceId = (record: {
  name: string
  stxResourceId?: string
}): string =>
  record.stxResourceId ? `${record.stxResourceId}: ${record.name}` : record.name

// Until its records load, an id stands in rather than calling a value deleted.
const unresolved = (id: string, isLoaded: boolean): IResourceChipItem =>
  isLoaded ? { id, isDeleted: true } : { id, label: id }

export default observer(FieldValuesSelector)

function FieldValuesSelector({
  filter,
  selectedField,
  isEditMode,
  restrictedLocationIds,
  restrictedLocationTypes,
  onChange,
}: IProps) {
  const isDisabled = !isEditMode || !selectedField || !filter.fieldId
  const { state, locationAttributesStore } = React.useContext(
    MobXProviderContext,
  ) as { state: InitialState; locationAttributesStore: LocationAttributesStore }
  const [isOpen, setIsOpen] = React.useState(false)
  const { resourceTypeId, resourceLabel, recordPredicate } =
    useResourceValueSelection({
      fieldType: selectedField?.type,
      restrictedIds: restrictedLocationIds,
      restrictedTypes: restrictedLocationTypes,
      selectedIds: filter.fieldValues,
    })

  const materials = useMaterialResources({ projectId: state.activeProject?.id })
  const materialsById = React.useMemo(
    () =>
      new Map(materials.recordsWithDeletedItems.map(r => [String(r.id), r])),
    [materials.recordsWithDeletedItems],
  )

  const toChipItem = (id: string): IResourceChipItem => {
    if (resourceTypeId === ResourceTypeId.MATERIALS) {
      const record = materialsById.get(String(id))
      return record
        ? { id, label: withResourceId(record), isDeleted: record.isDeleted }
        : unresolved(id, materials.isReady)
    }
    const location = locationAttributesStore.allItemsMapWithDeleted[
      id
    ] as LocationBase
    if (!location) {
      return unresolved(id, locationAttributesStore.isDataReceived)
    }
    return {
      id,
      label: withResourceId(location),
      isDeleted: location.isDeleted,
      color: location.color,
      iconName: location.iconName,
    }
  }
  const values = filter.fieldValues.filter(Boolean)

  const renderIcon = (item: IResourceChipItem) =>
    renderResourceRecordIcon(
      resourceTypeId,
      item,
      resourceTypeId === ResourceTypeId.MATERIALS ? (
        <PackageIcon className={ICON_CLASS_NAME} />
      ) : (
        renderResourceSemanticIcon(
          SEMANTIC_ICON[resourceTypeId],
          ICON_CLASS_NAME,
        )
      ),
      ICON_CLASS_NAME,
    )

  const handleApply = (ids: string[]) => {
    onChange(ids)
    setIsOpen(false)
  }

  const removeValue = (id: string) =>
    onChange(filter.fieldValues.filter(value => value !== id))

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger
        nativeButton={false}
        disabled={isDisabled}
        render={
          <div className="tw:my-0.5 tw:min-w-40 tw:max-w-max tw:flex-1! tw:rounded tw:focus-visible:outline-2 tw:focus-visible:outline-ring" />
        }
      >
        <StruxhubResourceSelector
          isLabelHidden
          isHelperTextHidden
          isHelperTextCondensed
          isDisabled={isDisabled}
          isViewMode={!isEditMode}
          items={values.map(toChipItem)}
          renderIcon={renderIcon}
          onClick={() => setIsOpen(true)}
          onRemove={removeValue}
        />
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        collisionAvoidance={{ fallbackAxisSide: 'none' }}
        className="tw:w-[400px] tw:overflow-hidden tw:p-0"
      >
        <ResourceTypeSelectionContent
          resourceTypeId={resourceTypeId}
          projectId={state.activeProject?.id}
          resourceLabel={resourceLabel}
          selectedIds={filter.fieldValues}
          recordPredicate={recordPredicate}
          allowEmptySelection={true}
          onApply={handleApply}
          onClose={() => setIsOpen(false)}
        />
      </PopoverContent>
    </Popover>
  )
}
